import { StackScreenProps } from "@react-navigation/stack";
import {
  ApiKeyVaultRootParamList,
  CreateNewApiVaultKeyRootParamList,
} from "../../../../types";
import { useTranslation } from "react-i18next";
import {
  Card,
  Divider,
  FlatActionList,
  Page,
  Stack,
  TextButton,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useFormContext } from "react-hook-form";
import {
  ApiVaultKeyForm,
  ApiVaultKeyFormValues,
} from "../../components/ApiVaultKeyForm";
import { Colors } from "react-native-ui-lib";
import { useMutation } from "@apollo/client";
import { MUTATION_STORE_USER_API_KEY } from "../../../../utilities/user-api-keys";
import { UserApiKey } from "@towersystems/roam-common/lib/generated-types";

export interface CreateNewApiVaultKeyScreenProps
  extends StackScreenProps<
    CreateNewApiVaultKeyRootParamList,
    "CreateNewApiVaultKeyScreen"
  > {}

export const CreateNewApiVaultKeyScreen = ({
  navigation,
}: CreateNewApiVaultKeyScreenProps) => {
  const { t } = useTranslation("api_key_vault");
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    storeUserApiKey: UserApiKey;
  }>(MUTATION_STORE_USER_API_KEY);

  const { handleSubmit, watch, formState } =
    useFormContext<ApiVaultKeyFormValues>();

  const watchedKeyType = watch("apiKeyType");

  const { isDirty, isLoading, isSubmitting, isValid } = formState;

  const disabled = !isDirty || isLoading || isSubmitting || !isValid;

  const handleOnSubmit = async (values: ApiVaultKeyFormValues) => {
    return m({
      variables: {
        input: {
          apiKeyType: values.apiKeyType,
          key: values.key,
        },
      },
    })
      .then((result) => {
        if (result?.data?.storeUserApiKey) {
          flashMessagesContext.showMessage({
            message: "API Key saved",
            severity: "success",
          });
          navigation.goBack();
          return;
        } else {
          flashMessagesContext.showMessage({
            message: "Could not save API Key",
            severity: "error",
          });
        }
      })
      .catch((err) => {
        console.log(err);
        flashMessagesContext.showMessage({
          message: err?.message ?? "Could not save API Key",
          severity: "error",
        });
      });
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_api_key")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={t("title.create_new_api_vault_key")}
      secondaryTitle={t("title.create_new_api_vault_key_secondary")}
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title={t("card.api_key_vault_details.title")}>
          <FlatActionList
            items={[
              {
                title: "API Integration Key Type",
                onPress: () => navigation.navigate("SelectApiKeyTypeScreen"),
                key: "select-filter-type",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("SelectApiKeyTypeScreen")
                      }
                    >
                      {watchedKeyType || "Set API key Type"}
                    </TextButton>
                  );
                },
              },
            ]}
          />
          <Divider />
          <ApiVaultKeyForm.KeyInputField />
        </Card>
      </Stack>
    </Page>
  );
};
